import { useLayoutEffect, type RefObject } from "react";

/**
 * Publishes an element's border-box height as a CSS custom property on the
 * document root, so other elements can reserve space for it. Useful when an
 * element is taken out of flow and its height cannot be expressed in CSS.
 */
export const usePublishedHeight = (
  ref: RefObject<HTMLElement>,
  property: string
) => {
  useLayoutEffect(() => {
    const element = ref.current;
    if (element == null) {
      return;
    }

    const root = element.ownerDocument.documentElement;

    const observer = new ResizeObserver(([entry]) => {
      // borderBoxSize includes padding and border; contentRect would not.
      const height =
        entry.borderBoxSize?.[0]?.blockSize ??
        element.getBoundingClientRect().height;
      root.style.setProperty(property, `${height}px`);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      root.style.removeProperty(property);
    };
  }, [ref, property]);
};
